import { useState } from "react";
import { X } from "lucide-react";
import { PasswordInput } from "./PasswordInput";
import { apiFetch } from "../data/session";
import useEscape from "../hooks/useEscape";

const ChangePasswordDialog = ({ token, onClose }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  useEscape(onClose);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (next.length < 8) return setError("Le nouveau mot de passe doit avoir au moins 8 caractères.");
    if (next !== again) return setError("Les deux nouveaux mots de passe ne sont pas identiques.");
    setBusy(true);
    try {
      await apiFetch("/api/auth/password/change", token, {
        method: "POST",
        body: JSON.stringify({ currentPassword: current, newPassword: next }),
      });
      setDone(true);
    } catch (problem) {
      setError(problem.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const field =
    "mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#25D366]";
  const label = "mt-4 block text-xs font-bold uppercase tracking-wider text-[#189346]";
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0E1A14]/55 p-4" data-testid="change-password-dialog">
      <form onSubmit={submit} className="relative w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl">
        <button
          type="button"
          aria-label="Fermer"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-[#4a5f53] hover:bg-[#F3FCF7]"
        >
          <X className="h-5 w-5" />
        </button>
        <h2 className="font-display text-2xl font-bold text-[#122118]">Changer le mot de passe</h2>
        {done ? (
          <>
            <p className="mt-3 text-sm text-[#2d5a3d]" data-testid="change-password-done">
              C’est fait. Vos autres appareils ont été déconnectés.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 w-full rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-[#0a2914]"
            >
              Fermer
            </button>
          </>
        ) : (
          <>
            <label className={label} htmlFor="cp-current">Mot de passe actuel</label>
            <PasswordInput id="cp-current" testId="change-password-current" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
            <label className={label} htmlFor="cp-new">Nouveau mot de passe</label>
            <PasswordInput id="cp-new" testId="change-password-new" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} className={field} />
            <label className={label} htmlFor="cp-again">Encore une fois</label>
            <PasswordInput id="cp-again" testId="change-password-again" autoComplete="new-password" required value={again} onChange={(e) => setAgain(e.target.value)} className={field} />
            {error && <p className="mt-3 text-sm text-[#9b3b3b]" data-testid="change-password-error">{error}</p>}
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm font-semibold text-[#4a5f53]">
                Annuler
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="change-password-submit"
                className="rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-semibold text-[#0a2914] disabled:opacity-50"
              >
                Enregistrer
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default ChangePasswordDialog;
